import {
  contractLabel,
  formatDisplayDate,
  i18nKeyForError,
  parseApiError,
  professionalLabel,
  SearchCombobox,
  type SelectedOption,
} from '@features/allocation';
import {
  Alert,
  AlertDialog,
  Button,
  FormField,
  Input,
  Modal,
  ScrollArea,
  Switch,
  useToast,
} from '@ntconsult/aluma-react';
import { type Ref, useId, useImperativeHandle, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { createManagerSearch } from '../hooks/useManagers';
import { useProjectStatusLabel } from '../hooks/useProjectStatusLabel';
import { useUpdateProject } from '../hooks/useUpdateProject';
import type { ProjectRow } from '../types/ProjectList';

/** Identidade estável dos hooks de busca (um `useQuery` por render, só o texto muda). */
const searchProjectManager = createManagerSearch('projectManager');
const searchOperationManager = createManagerSearch('operationManager');
const searchAccountDirector = createManagerSearch('accountDirector');

type EditProjectModalProps = {
  open: boolean;
  onClose: () => void;
  /** `null` enquanto nenhuma linha está selecionada. */
  project: ProjectRow | null;
};

type FormValues = {
  projectManager: SelectedOption | null;
  operationManager: SelectedOption | null;
  accountDirector: SelectedOption | null;
  isInternal: boolean;
  isPool: boolean;
};

const managerOption = (manager: ProjectRow['projectManager']): SelectedOption | null =>
  manager ? { id: manager.id, name: professionalLabel(manager) } : null;

function initialValues(project: ProjectRow): FormValues {
  return {
    projectManager: managerOption(project.projectManager),
    operationManager: managerOption(project.operationManager),
    accountDirector: managerOption(project.accountDirector),
    isInternal: project.isInternal,
    isPool: project.isPool,
  };
}

/** O modal lê o form só ao fechar (sem espelhar `isDirty` em estado). */
type FormBodyHandle = { isDirty: boolean };

type FormBodyProps = {
  project: ProjectRow;
  onClose: () => void;
  /** Cancelar/X: pode pedir confirmação se houver alteração. */
  onCancel: () => void;
  ref: Ref<FormBodyHandle>;
};

/**
 * Edição por admin, copiando o `Cadastro de Projeto` do legado: o formulário
 * completo aparece, mas só os 3 gerentes, Interno e Pool são editáveis — os
 * demais campos vêm do Access BR/US e ficam desabilitados.
 */
function FormBody({ project, onClose, onCancel, ref }: FormBodyProps) {
  const { t, i18n } = useTranslation(['projetos', 'alocacao', 'common']);
  const { toast } = useToast();
  const statusLabel = useProjectStatusLabel();
  const updateProject = useUpdateProject();

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<FormValues>({ mode: 'onChange', defaultValues: initialValues(project) });

  useImperativeHandle(ref, () => ({ isDirty }), [isDirty]);

  const onSubmit = handleSubmit((values) => {
    updateProject.mutate(
      {
        id: project.id,
        body: {
          projectManagerId: values.projectManager?.id ?? null,
          operationManagerId: values.operationManager?.id ?? null,
          accountDirectorId: values.accountDirector?.id ?? null,
          isInternal: values.isInternal,
          isPool: values.isPool,
        },
      },
      {
        onSuccess: () => {
          toast.success({ title: t('projetos:edicao.toastSucesso') });
          onClose();
        },
        onError: (error) => {
          const apiError = parseApiError(error);
          toast.danger({
            title: apiError
              ? t(i18nKeyForError(apiError.code) as never)
              : t('projetos:edicao.toastErro'),
          });
        },
      },
    );
  });

  const readOnlyField = (label: string, value: string) => (
    <FormField label={label}>
      <Input disabled value={value} />
    </FormField>
  );

  return (
    <form onSubmit={onSubmit} className="contents" noValidate>
      <Modal.Body className="flex min-h-0 flex-col overflow-hidden px-0 py-0">
        <ScrollArea className="min-h-0 flex-1 px-6 py-3">
          <div className="flex flex-col gap-4 pr-2">
            <Alert variant="info" title={t('projetos:edicao.aviso')} />

            {/* Linha 1 — Id · Interno · Pool (grade do `ProjetoForm.php`). */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              {readOnlyField(t('projetos:edicao.id'), String(project.id))}
              <FormField id="projeto-interno" label={t('projetos:colunas.interno')}>
                <Controller
                  control={control}
                  name="isInternal"
                  render={({ field }) => (
                    <Switch
                      id="projeto-interno"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </FormField>
              <FormField id="projeto-pool" label={t('projetos:colunas.pool')}>
                <Controller
                  control={control}
                  name="isPool"
                  render={({ field }) => (
                    <Switch
                      id="projeto-pool"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </FormField>
            </div>

            {/* Linha 2 — Unidade · Contrato (só leitura). */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {readOnlyField(t('projetos:colunas.unidade'), project.unit.code)}
              {readOnlyField(
                t('projetos:colunas.contrato'),
                project.contract ? contractLabel(project.contract) : '—',
              )}
            </div>

            {/* Linha 3 — Cód. Axis · Nome · Situação (só leitura). */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              {readOnlyField(t('projetos:colunas.codigo'), project.axisCode)}
              <FormField label={t('projetos:colunas.nome')} className="md:col-span-2">
                <Input disabled value={project.name} />
              </FormField>
              {readOnlyField(t('projetos:colunas.situacao'), statusLabel(project.status))}
            </div>

            {/* Linha 4 — Diretor de Conta · Gerente de Operação · Gerente de Projeto (editáveis). */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <FormField id="projeto-diretor" label={t('projetos:colunas.diretorConta')}>
                <Controller
                  control={control}
                  name="accountDirector"
                  render={({ field }) => (
                    <SearchCombobox
                      id="projeto-diretor"
                      placeholder={t('projetos:edicao.selecionar')}
                      value={field.value}
                      onChange={(option) => field.onChange(option)}
                      useSearch={searchAccountDirector}
                      getLabel={professionalLabel}
                    />
                  )}
                />
              </FormField>
              <FormField
                id="projeto-gerente-operacao"
                label={t('projetos:colunas.gerenteOperacao')}
              >
                <Controller
                  control={control}
                  name="operationManager"
                  render={({ field }) => (
                    <SearchCombobox
                      id="projeto-gerente-operacao"
                      placeholder={t('projetos:edicao.selecionar')}
                      value={field.value}
                      onChange={(option) => field.onChange(option)}
                      useSearch={searchOperationManager}
                      getLabel={professionalLabel}
                    />
                  )}
                />
              </FormField>
              <FormField id="projeto-gerente-projeto" label={t('projetos:colunas.gerenteProjeto')}>
                <Controller
                  control={control}
                  name="projectManager"
                  render={({ field }) => (
                    <SearchCombobox
                      id="projeto-gerente-projeto"
                      placeholder={t('projetos:edicao.selecionar')}
                      value={field.value}
                      onChange={(option) => field.onChange(option)}
                      useSearch={searchProjectManager}
                      getLabel={professionalLabel}
                    />
                  )}
                />
              </FormField>
            </div>

            {/* Linha 5 — Início · Término (só leitura). */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              {readOnlyField(
                t('projetos:colunas.inicio'),
                project.startDate ? formatDisplayDate(project.startDate, i18n.language) : '—',
              )}
              {readOnlyField(
                t('projetos:colunas.termino'),
                project.endDate ? formatDisplayDate(project.endDate, i18n.language) : '—',
              )}
            </div>
          </div>
        </ScrollArea>
      </Modal.Body>

      <Modal.Footer className="border-t border-border">
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t('common:cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={!isDirty}
          loading={updateProject.isPending}
        >
          {t('common:save')}
        </Button>
      </Modal.Footer>
    </form>
  );
}

export function EditProjectModal({ open, onClose, project }: EditProjectModalProps) {
  const { t } = useTranslation(['projetos', 'alocacao', 'common']);
  const titleId = useId();
  const formRef = useRef<FormBodyHandle>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  function close() {
    setConfirmingDiscard(false);
    onClose();
  }

  function requestClose() {
    if (formRef.current?.isDirty) setConfirmingDiscard(true);
    else close();
  }

  return (
    <Modal
      open={open}
      onClose={requestClose}
      closeOnBackdrop={false}
      size="4xl"
      ariaLabelledBy={titleId}
    >
      <Modal.Header
        titleId={titleId}
        title={t('projetos:edicao.titulo')}
        onClose={requestClose}
        dismissLabel={t('common:close')}
      />
      {open && project && (
        <FormBody
          key={project.id}
          project={project}
          onClose={close}
          onCancel={requestClose}
          ref={formRef}
        />
      )}
      <AlertDialog
        open={confirmingDiscard}
        onOpenChange={(next) => !next && setConfirmingDiscard(false)}
      >
        <AlertDialog.Content>
          <AlertDialog.Title>{t('alocacao:form.descartarTitulo')}</AlertDialog.Title>
          <AlertDialog.Description>{t('alocacao:form.descartarDescricao')}</AlertDialog.Description>
          <AlertDialog.Footer>
            <AlertDialog.Cancel type="button">
              {t('alocacao:form.continuarEditando')}
            </AlertDialog.Cancel>
            <AlertDialog.Action type="button" variant="danger" onClick={close}>
              {t('alocacao:form.descartar')}
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
    </Modal>
  );
}
